<template>
  <div class="line-bar-mix-chart" :style="{ width: config.chartWidth + 'px', height: config.chartHeight + 'px' }">
    <div class="line-bar-mix-chart-wrapper">
      <img
        v-if="borderImgUrl"
        class="line-bar-mix-chart-border"
        :src="borderImgUrl"
        alt=""
        :style="chartBorderTemplateImageStyle">
      <div class="line-bar-mix-chart-title" :style="titleBarInlineStyle">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="mixChart" class="line-bar-mix-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64 } from '@/utils/common'
import { lineBaseOption } from './options'
import ChartMixin from './mixin'
import { getXAxisLabelRotateDegrees, gridBottomForXAxisLabelRotate } from '@/utils/chartAxisLabel'

export default {
  name: 'LineBarMixChart',
  mixins: [ChartMixin],

  computed: {
    chartData () {
      return this.config.chartViewData.chartData
    },
    style () {
      return this.config.chartSet
    },
    chartStyle () {
      return this.style.chartStyle
    },
    setAxis () {
      return this.chartStyle.axisConfig || null
    },
    dataAxis () {
      const list = []
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const colKeyList = Object.keys(this.chartData[0])
        this.chartData.slice(1).forEach((bar) => {
          list.push(bar[colKeyList[0]])
        })
        return list
      }
      return []
    },
    series () {
      if (!this.chartData?.length) return []
      const colKeyList = Object.keys(this.chartData[0]).slice(1)
      const tableHeader = this.chartData[0]
      const tableData = this.chartData.slice(1)
      const typeMap = this.chartStyle.seriesTypeByCol || {}
      const radius = this.chartStyle.borderRadius
      const bw = this.chartStyle.borderWidth
      const palette = COLOR_LIST.filter((item) => item.label === this.resolvedChartColorsLabel)[0]?.value || [
        '#0276FA',
        '#3ECB6F',
      ]
      const list = []
      let paletteIndex = 0

      colKeyList.forEach((key) => {
        if (tableData.every((row) => row[key] === '' || row[key] === undefined)) return
        const colorHex = palette[paletteIndex % palette.length]
        paletteIndex += 1

        const axisIndex =
          this.chartStyle.doubleAxis && this.setAxis && this.setAxis[key] != null
            ? this.setAxis[key]
            : 0
        const isBar = typeMap[key] === 'bar'
        const name = tableHeader[key]
        const data = tableData.map((row) => row[key])
        if (isBar) {
          list.push({
            type: 'bar',
            name,
            data,
            yAxisIndex: axisIndex ? 1 : 0,
            barGap: '30%',
            z: 2,
            label: {
              show: this.chartStyle.isLabel,
              position: 'top',
              color: '#D0D5E0',
              fontSize: this.chartStyle.fontSize,
            },
            barWidth: bw,
            itemStyle: {
              color: colorHex,
              borderRadius: [radius, radius, 0, 0],
            },
            emphasis: {
              focus: 'series',
              itemStyle: { color: colorHex },
            },
          })
        } else {
          list.push({
            type: 'line',
            name,
            data,
            yAxisIndex: axisIndex ? 1 : 0,
            smooth: this.chartStyle.smooth,
            symbol: 'circle',
            symbolSize: 6,
            z: 3,
            lineStyle: {
              width: 2,
              color: colorHex,
              ...(axisIndex ? { type: 'dotted' } : {}),
            },
            itemStyle: {
              color: colorHex,
            },
            emphasis: {
              focus: 'series',
              lineStyle: { color: colorHex, width: 2 },
              itemStyle: { color: colorHex },
            },
          })
        }
      })
      return list
    },
    xAxisLabelLayoutSignature () {
      const cs = this.chartStyle
      if (!cs) return '0|5'
      const rot = Number(cs.xAxisLabelRotate)
      const bp = Number(cs.xAxisLabelRotateBottomPercent)
      return `${Number.isFinite(rot) ? rot : 0}|${Number.isFinite(bp) ? bp : 5}`
    },
  },

  watch: {
    resolvedChartColorsLabel () {
      if (!this.charts) return
      // 系列色写在各 series 上，需整表重算（与折线切换全局配色一致）
      this.init()
    },
    // 与 lineChart 一致：勿在 handler 内无条件 $set config，否则会与 ensureSeriesTypeByColKeys 形成死循环
    config: {
      handler () {
        this.init()
      },
      deep: true,
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
    xAxisLabelLayoutSignature () {
      if (!this.charts) return
      this.$nextTick(() => this.init())
    },
  },

  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    if (!this.chartStyle.carouselDelay) {
      this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3)
    }
    if (!this.chartStyle.legendSize) {
      this.$set(this.config.chartSet.chartStyle, 'legendSize', 12)
    }
    if (this.chartStyle.smooth === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'smooth', false)
    }
    if (this.chartStyle.borderWidth === undefined || this.chartStyle.borderWidth === null) {
      this.$set(this.config.chartSet.chartStyle, 'borderWidth', 10)
    }
    if (this.chartStyle.borderRadius === undefined || this.chartStyle.borderRadius === null) {
      this.$set(this.config.chartSet.chartStyle, 'borderRadius', 10)
    }
    if (!this.chartStyle.seriesTypeByCol || typeof this.chartStyle.seriesTypeByCol !== 'object') {
      this.$set(this.config.chartSet.chartStyle, 'seriesTypeByCol', {})
    }
    if (this.chartStyle.xAxisLabelRotate === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'xAxisLabelRotate', 0)
    }
    if (this.chartStyle.xAxisLabelRotateBottomPercent === undefined) {
      this.$set(this.config.chartSet.chartStyle, 'xAxisLabelRotateBottomPercent', 5)
    }
    this.ensureSeriesTypeByColKeys()
  },

  mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.mixChart)
    }
    this.init()
  },

  methods: {
    /** 表头列变化时补齐 / 清理 seriesTypeByCol；仅在内容相对当前真有变化时才 $set，避免触发 config 深度监听死循环 */
    ensureSeriesTypeByColKeys () {
      const row0 = this.chartData?.[0]
      if (!row0 || typeof row0 !== 'object') return
      const keys = Object.keys(row0).slice(1)
      const cs = this.config.chartSet.chartStyle
      if (!cs.seriesTypeByCol || typeof cs.seriesTypeByCol !== 'object') {
        this.$set(cs, 'seriesTypeByCol', {})
      }
      const cur = { ...cs.seriesTypeByCol }
      keys.forEach((k) => {
        if (cur[k] !== 'bar' && cur[k] !== 'line') {
          cur[k] = 'line'
        }
      })
      Object.keys(cur).forEach((k) => {
        if (!keys.includes(k)) delete cur[k]
      })
      if (!_.isEqual(cs.seriesTypeByCol, cur)) {
        this.$set(cs, 'seriesTypeByCol', cur)
      }
    },

    init () {
      this.ensureSeriesTypeByColKeys()
      this.option = _.merge(_.cloneDeep(lineBaseOption), {
        grid: {
          top: this.chartStyle.isLegend ? '46px' : '30px',
          bottom: gridBottomForXAxisLabelRotate(this.chartStyle, '10%'),
        },
        tooltip: {
          trigger: 'axis',
          textStyle: {
            fontSize: this.chartStyle.fontSize,
          },
        },
        color: COLOR_LIST.filter((item) => item.label === this.resolvedChartColorsLabel)[0].value,
        xAxis: {
          data: this.dataAxis,
          axisLabel: {
            fontSize: this.chartStyle.fontSize,
            rotate: getXAxisLabelRotateDegrees(this.chartStyle),
          },
        },
        legend: {
          show: this.chartStyle.isLegend,
          textStyle: {
            fontSize: this.chartStyle.legendSize,
          },
        },
        yAxis: this.chartStyle.doubleAxis
          ? [
            { axisLabel: { fontSize: this.chartStyle.fontSize } },
            { axisLabel: { fontSize: this.chartStyle.fontSize } },
          ]
          : [{ axisLabel: { fontSize: this.chartStyle.fontSize } }],
        label: {
          show: this.chartStyle.isLabel,
          fontSize: this.chartStyle.fontSize,
        },
        series: this.series,
      })

      const gt = this.chartStyle.isLegend ? '46px' : '30px'
      const gb = gridBottomForXAxisLabelRotate(this.chartStyle, '10%')
      this.option.grid = { ...this.option.grid, top: gt, bottom: gb }

      this.charts.setOption(this.option, true)

      if (this.chartStyle.carousel && this.series?.length > 0) {
        this.tooltipsActive()
        this.mouseEvents()
      } else {
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.timer) {
          clearInterval(this.timer)
          this.index = -1
        }
      }
      this.$nextTick(() => {
        this.charts.resize()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.line-bar-mix-chart {
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width: 100%;
    overflow: visible;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;
  }
  &-title {
    padding: 7px 30px;
  }
  .line-bar-mix-chart-container {
    flex: 1;
  }
}
</style>
